// AppSliceSchema.ts

import {
    validators,
    ApiSurveyCompleteResSchema,
    ApiGlobalRewardActivityResSchema,
    CheckInScheduleSchema,
} from "@bridge-applications/eh-types";
import { z } from "zod";

const Eip712Domain = z.object({
    name: z.string(),
    version: z.string(),
    chainId: z.number(),
    verifyingContract: z.string(),
});

const Eip712VoucherStruct = z.object({
    to: z.string(),
    tokenType: z.number(), // 0..3
    token: z.string(), // address
    amount: z.string(), // uint256 decimal string
    tokenId: z.string(), // uint256 decimal string
    expiresAt: z.number(), // seconds
    nonce: z.number(),
});

const Eip712Voucher = z.object({
    struct: Eip712VoucherStruct,
    signature: z.string(),
    domain: Eip712Domain,
});

export const StwVoucherAvailableSchema = z.object({
    /** Which campaign this voucher belongs to (matches a feed item) */
    campaignId: z.string(),
    /** Server spin id (useful for telemetry / support) */
    spinId: z.string(),
    /** Prize key we’ll match against feedSlice.items[i].prizes[].key */
    prizeKey: z.string(),
    /** Cooldown end (ISO). After this the voucher is no longer valid. */
    canSpinAgainAt: z.string(),
    /** When we saved it locally (ISO) */
    savedAt: z.string(),
    /** The signed EIP-712 voucher returned by the backend */
    voucher: Eip712Voucher,
    /** Optional passthrough; not required to render since feed already has it */
    ui: z
        .object({
            tier: z.string().optional(),
            color: z.string().optional(),
            iconUrl: z.string().optional(),
            iconUrlS: z.string().optional(),
            label: z.string().optional(),
        })
        .optional(),
});

export const AppSliceSchema = z.object({
    notificationsEnabled: z.boolean(),
    walletAddress: z.string().nullable(),
    username: z.string().nullable(),
    profilePictureUrl: z.string().nullable(),
    loading: z
        .object({
            appSession: z.boolean(),
            submittingTask: z.boolean(),
            wheelLoading: z.boolean(),
        })
        .optional(),
    checkInVoucherAvailable: z
        .object({
            unit: z.string(),
            amount: z.string(),
            nonce: z.string(),
            signature: z.string(),
        })
        .nullable(),
    /**
     * Full set of today's check-in vouchers (multi-token), carrying
     * EhUniversalVoucher payloads. Cleared after claim.
     */
    checkInRewards: z
        .array(
            z.object({
                tokenAddress: z.string(),
                amount: z.string(),
                label: z.string().optional(),
                voucher: z.object({
                    struct: z.object({
                        to: z.string(),
                        tokenType: z.number(),
                        token: z.string(),
                        amount: z.string(),
                        tokenId: z.string(),
                        expiresAt: z.number(),
                        nonce: z.number(),
                    }),
                    signature: z.string(),
                    domain: z.object({
                        name: z.string(),
                        version: z.string(),
                        chainId: z.number(),
                        verifyingContract: z.string(),
                    }),
                }),
            }),
        )
        .nullable(),
    ratingVoucherAvailable: z
        .object({
            unit: z.string(),
            amount: z.string(),
            nonce: z.string(),
            signature: z.string(),
        })
        .nullable(),
    stwVoucherAvailable: StwVoucherAvailableSchema.nullable(),
    notiVoucherAvailable: z
        .object({
            unit: z.string(),
            amount: z.string(),
            nonce: z.string(),
            signature: z.string(),
        })
        .nullable(),
    taskRewards: z
        .array(
            z.object({
                taskId: z.string(),
                addedAt: z.number(),
                // Reuse server response schema for rewards shape
                rewards: ApiSurveyCompleteResSchema.shape.rewards,
            }),
        )
        .optional(),
    streak: z
        .object({
            currentCount: z.number(),
            topCount: z.number(),
            mostRecent14Ts: z.array(validators.timestampMs()),
        })
        .nullable(),
    /** Full check-in reward schedule (static reward config + per-day claim status). */
    checkInSchedule: CheckInScheduleSchema.nullable(),
    totalTasksCompleted: z.number().nullable(),
    totalRewardsEarned: z.number().nullable(),
    qaScore: z.number().nullable(),
    /**
     * Latest global reward-activity feed we fetch alongside app-session.
     */
    globalRewardActivity:
        ApiGlobalRewardActivityResSchema.shape.data.nullable(),
});
